<!--
  Super Admin: distributor invoices -- the 1st/15th review and release surface.
  Called Decoration Invoices until Trello #563 (Jake, 2026-10-28) added a Net
  Checkout order's platform fee and shipping to each line. Trello #618 added
  a payroll-deduction order's platform fee (Jake, 2026-10-29) and its whole
  shipping and handling (Jake, 2026-10-30).

  Plan: docs/planning/2026-09-02-stripe-payments-payouts-implementation-plan.md
  section "Unit 7 -- Decoration invoicing on the 1st and 15th".
  Requirements: NET-04, NET-05, OPS-06, OPS-08.

  WHY THIS PAGE IS LOAD-BEARING, not a report.

  The scheduled batch assembles DRAFTS and stops. Nothing reaches Stripe until
  a human releases a batch here (OPS-08 -- "a human always looks before it
  ships"). So this surface is the second half of the batch: if it does not
  work, decoration, platform fees and shipping and handling on Net Checkout
  and payroll orders silently stop being collected.

  ONE ACTION (Jake, 2026-11-03): "Approve and issue".

      draft --(approve and issue)--> issued
                       |
                       +-- Stripe failed --> approved --(Retry issue)--> issued

  The approval is the human review; the same request issues the invoice at
  Stripe, so there is no second click. If the distributor has no verified
  payment method the server refuses up front (422) and the invoice stays a
  draft, with the server's message shown on the row. If Stripe fails after
  the approval the invoice is left APPROVED and the row offers Retry issue,
  which posts to the release route: the same idempotent issue path, so a
  retry never bills twice. The server enforces every transition; the buttons
  here follow its rules rather than defining them. Hiding Retry on a draft
  row is an AFFORDANCE, not a control -- the control is the 409 the endpoint
  returns, pinned by api/tests/test_distributor_invoice_ops_surface.py.

  TRACEABILITY (NET-04, OPS-06): every expanded invoice shows one row per
  source order, with the order key (TF-{order id}) a human can search in
  tf-deconetwork, and the components that sum to the billed amount:
  decorator payout, ThreadFlow decoration charge, platform fee, shipping
  and handling (Net Checkout and payroll alike), and the card
  share shortfall (Trello #621): the part of ThreadFlow's share a CARD
  order's charge could not carry. On a card order's line it is the only
  non-zero component; on every other line it is zero.

  ASCII-only.
-->
<template>
    <div data-testid="sa-distributor-invoices">
        <h1 class="sa-page-title">Distributor Invoices</h1>

        <p class="sa-di-intro" data-testid="sa-distributor-invoices-intro">
            What distributors are billed on the 1st and the 15th for orders
            that have shipped in full: decoration, platform fee, and shipping
            and handling on Net Checkout and payroll-deduction orders. Batches
            are assembled automatically and stay as drafts until a person
            approves them. Approving issues the invoice to Stripe; nothing is
            sent to Stripe before that.
        </p>

        <div class="sa-di-toolbar">
            <button
                class="sa-di-btn"
                data-testid="sa-distributor-invoices-run-batch"
                :disabled="busy"
                @click="runBatch"
            >
                Assemble drafts for this period
            </button>
            <label class="sa-di-filter">
                Status
                <select
                    v-model="statusFilter"
                    class="sa-di-filter-select"
                    data-testid="sa-distributor-invoices-status-filter"
                    :disabled="busy || loading"
                    @change="fetchInvoices"
                >
                    <option
                        v-for="opt in STATUS_OPTIONS"
                        :key="opt.value"
                        :value="opt.value"
                    >
                        {{ opt.label }}
                    </option>
                </select>
            </label>
            <span
                v-if="batchSummary"
                class="sa-di-summary"
                data-testid="sa-distributor-invoices-batch-summary"
            >
                {{ batchSummary }}
            </span>
        </div>

        <!--
          The outcome of this session's Approve and issue / Issue now / Retry
          issue: the server's own message on a refusal or a Stripe failure (it
          names the distributor and what to do next), or Issued with the due
          date. OUTSIDE the list on purpose: a status filter can drop the row
          on the refetch that follows, and the outcome must not go with it.
        -->
        <div
            v-if="outcomeIds.length"
            class="sa-di-outcomes"
            data-testid="sa-distributor-invoices-outcomes"
        >
            <template v-for="id in outcomeIds" :key="id">
                <p
                    v-if="actionErrors[id]"
                    class="sa-di-outcome sa-di-outcome--error"
                    :data-testid="`sa-distributor-invoice-action-error-${id}`"
                >
                    Invoice #{{ id }}: {{ actionErrors[id] }}
                </p>
                <p
                    v-else
                    class="sa-di-outcome sa-di-outcome--ok"
                    :data-testid="`sa-distributor-invoice-result-${id}`"
                >
                    Invoice #{{ id }}: {{ actionResults[id] }}
                </p>
            </template>
        </div>

        <p
            v-if="loading"
            class="sa-di-state"
            data-testid="sa-distributor-invoices-loading"
        >
            Loading...
        </p>
        <p
            v-else-if="errorMsg"
            class="sa-di-state sa-di-state--error"
            data-testid="sa-distributor-invoices-error"
        >
            {{ errorMsg }}
        </p>
        <p
            v-else-if="invoices.length === 0"
            class="sa-di-state"
            data-testid="sa-distributor-invoices-empty"
        >
            <template v-if="statusFilter">
                No invoices with that status. Choose "All statuses" to see
                every invoice.
            </template>
            <template v-else>
                No distributor invoices yet. They appear here after a batch
                runs on the 1st or the 15th.
            </template>
        </p>

        <table v-else class="sa-table" data-testid="sa-distributor-invoices-table">
            <thead>
                <tr>
                    <th>Invoice</th>
                    <th>Distributor</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Due</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="inv in invoices" :key="inv.id">
                    <tr :data-testid="`sa-distributor-invoice-row-${inv.id}`">
                        <td>
                            <button
                                class="sa-di-link"
                                :data-testid="`sa-distributor-invoice-expand-${inv.id}`"
                                @click="toggleLines(inv.id)"
                            >
                                #{{ inv.id }}
                            </button>
                        </td>
                        <td :data-testid="`sa-distributor-invoice-distributor-${inv.id}`">
                            <DistributorLink
                                :distributor-id="inv.distributor_id"
                                :name="inv.distributor_name"
                                :fallback="`Distributor #${inv.distributor_id}`"
                            />
                        </td>
                        <td>{{ formatDay(inv.period_start) }} to {{ formatDay(inv.period_end) }}</td>
                        <td>
                            <span
                                class="sa-di-status"
                                :class="statusClass(inv.status)"
                                :data-testid="`sa-distributor-invoice-status-${inv.id}`"
                            >{{ inv.status }}</span>
                        </td>
                        <td>{{ formatMoney(inv.total_cents) }}</td>
                        <td>{{ inv.due_at ? formatDay(inv.due_at) : "--" }}</td>
                        <td>
                            <button
                                v-if="inv.status === 'draft'"
                                class="sa-di-btn sa-di-btn--small sa-di-btn--primary"
                                :data-testid="`sa-distributor-invoice-approve-${inv.id}`"
                                :disabled="busy"
                                @click="approve(inv)"
                            >
                                {{ pendingId === inv.id ? "Issuing..." : "Approve and issue" }}
                            </button>
                            <!--
                              An APPROVED invoice, or a stale ISSUING one the
                              server marks resumable (its process died; the
                              server owns the timeout). "Retry issue" once an
                              issue has failed, "Issue now" for one approved
                              before approve-and-issue shipped.
                            -->
                            <button
                                v-if="inv.status === 'approved' || inv.resumable"
                                class="sa-di-btn sa-di-btn--small sa-di-btn--primary"
                                :data-testid="`sa-distributor-invoice-issue-${inv.id}`"
                                :disabled="busy"
                                @click="retryIssue(inv)"
                            >
                                {{ pendingId === inv.id ? "Issuing..." : issueLabel(inv) }}
                            </button>
                        </td>
                    </tr>

                    <!--
                      The exception reason is shown on its own row rather than
                      truncated into a cell: the canonical case is "this
                      distributor never finished ACH setup", and it names the
                      action an operator has to take.
                    -->
                    <tr
                        v-if="inv.exception_reason"
                        :data-testid="`sa-distributor-invoice-exception-${inv.id}`"
                        class="sa-di-exception-row"
                    >
                        <td colspan="7">{{ inv.exception_reason }}</td>
                    </tr>

                    <!--
                      A line that bills LESS platform fee or shipping than the
                      order recorded at checkout: legitimate after an in-batch
                      edit, a red flag otherwise. Either way it must be seen
                      before anyone approves the draft (PR #4921 security
                      review). The server computes the flag.
                    -->
                    <tr
                        v-if="inv.lines_below_obligation > 0"
                        :data-testid="`sa-distributor-invoice-below-obligation-${inv.id}`"
                        class="sa-di-warning-row"
                    >
                        <td colspan="7">
                            {{ inv.lines_below_obligation }}
                            {{ inv.lines_below_obligation === 1 ? "line bills" : "lines bill" }}
                            less platform fee or shipping than the order recorded at
                            checkout. Expand the invoice and check each one before
                            approving.
                        </td>
                    </tr>

                    <tr
                        v-if="expanded[inv.id]"
                        :data-testid="`sa-distributor-invoice-lines-${inv.id}`"
                    >
                        <td colspan="7">
                            <table class="sa-di-lines">
                                <thead>
                                    <tr>
                                        <th>Order</th>
                                        <th>Decorator payout</th>
                                        <th>ThreadFlow charge</th>
                                        <th>Platform fee</th>
                                        <th>Shipping</th>
                                        <th>Card share shortfall</th>
                                        <th>Billed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template
                                        v-for="line in expanded[inv.id]"
                                        :key="line.id"
                                    >
                                        <tr :data-testid="`sa-distributor-invoice-line-${line.id}`">
                                            <td>{{ line.order_key }}</td>
                                            <td>{{ formatMoney(line.decoration_cost_cents) }}</td>
                                            <td>{{ formatMoney(line.decoration_charge_cents) }}</td>
                                            <td :data-testid="`sa-distributor-invoice-line-platform-fee-${line.id}`">
                                                {{ formatMoney(line.platform_fee_cents) }}
                                            </td>
                                            <td :data-testid="`sa-distributor-invoice-line-shipping-${line.id}`">
                                                {{ formatMoney(line.shipping_cents) }}
                                            </td>
                                            <td :data-testid="`sa-distributor-invoice-line-card-shortfall-${line.id}`">
                                                {{ formatMoney(line.card_shortfall_cents) }}
                                            </td>
                                            <td :data-testid="`sa-distributor-invoice-line-billed-${line.id}`">
                                                {{ formatMoney(line.amount_cents) }}
                                            </td>
                                        </tr>
                                        <tr
                                            v-if="shortfallText(line)"
                                            :data-testid="`sa-distributor-invoice-line-below-obligation-${line.id}`"
                                            class="sa-di-warning-row"
                                        >
                                            <td colspan="7">
                                                Bills less than checkout recorded:
                                                {{ shortfallText(line) }}.
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import DistributorLink from "@/components/superadmin/DistributorLink.vue";
import api from "@/services/api";

function formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return `$${value.toFixed(2)}`;
}

// Every status the API can filter on (?status=), in lifecycle order, with the
// label the operator reads. Mirrors INVOICE_STATUSES in
// api/src/models/distributor_invoice.py (pinned by a vitest spec and an API
// test; add a status there and it must be added here).
const STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "draft", label: "Draft" },
    { value: "approved", label: "Approved" },
    { value: "issuing", label: "Issuing" },
    { value: "issued", label: "Issued" },
    { value: "paid", label: "Paid" },
    { value: "payment_failed", label: "Payment failed" },
    { value: "needs_review", label: "Needs review" },
    { value: "uncollectible", label: "Uncollectible" },
    { value: "void", label: "Void" },
    { value: "exception", label: "Exception" },
];

// Statuses where the invoice is parked and a person has to act. They share
// the exception treatment so a stuck invoice stands out in the list.
const ATTENTION_STATUSES = new Set(["exception", "payment_failed", "needs_review"]);

function statusClass(status) {
    const classes = [`sa-di-status--${status}`];
    if (ATTENTION_STATUSES.has(status)) classes.push("sa-di-status--attention");
    if (status === "uncollectible") classes.push("sa-di-status--written-off");
    return classes;
}

// Payload key -> the words the warning uses.
const SHORTFALL_LABELS = { platform_fee: "platform fee", shipping: "shipping" };

function shortfallText(line) {
    const flags = (line && line.below_obligation) || {};
    return Object.keys(SHORTFALL_LABELS)
        .filter((key) => flags[key])
        .map(
            (key) =>
                `${SHORTFALL_LABELS[key]} ${formatMoney(flags[key].billed_cents)} ` +
                `(checkout recorded ${formatMoney(flags[key].obligation_cents)})`
        )
        .join("; ");
}

function formatDay(iso) {
    if (!iso) return "--";
    return String(iso).slice(0, 10);
}

// The success line for an issue response. An invoice the server parked as an
// exception (the backstop when the payment method vanished mid-request) is
// not "issued", so the line says so; its reason shows on the exception row.
function issuedText(invoice) {
    if (!invoice || invoice.status !== "issued") {
        const status = (invoice && invoice.status) || "unchanged";
        return `Not issued: the invoice is ${status}.`;
    }
    return invoice.due_at ? `Issued. Due ${formatDay(invoice.due_at)}.` : "Issued.";
}

export default {
    components: { DistributorLink },
    name: "SuperAdminDistributorInvoices",
    data() {
        return {
            loading: true,
            busy: false,
            statusFilter: "",
            STATUS_OPTIONS,
            errorMsg: "",
            batchSummary: "",
            invoices: [],
            // invoice id -> lines array, populated on expand. Lines are
            // fetched lazily because the list view is a triage surface and
            // one request per invoice would make it unusable at volume.
            expanded: {},
            // The invoice an Approve and issue / Retry issue is in flight for.
            pendingId: null,
            // invoice id -> the last action's outcome on that row.
            actionErrors: {},
            actionResults: {},
        };
    },
    computed: {
        // Invoices with an outcome to show, newest action last.
        outcomeIds() {
            return [
                ...new Set([
                    ...Object.keys(this.actionErrors),
                    ...Object.keys(this.actionResults),
                ]),
            ];
        },
    },
    async mounted() {
        await this.fetchInvoices();
    },
    methods: {
        formatMoney,
        formatDay,
        shortfallText,
        statusClass,
        issueLabel(invoice) {
            return invoice.failed_issue_attempt || invoice.resumable
                ? "Retry issue"
                : "Issue now";
        },
        async fetchInvoices() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await api.get("/superadmin/distributor-invoices", {
                    params: this.statusFilter ? { status: this.statusFilter } : {},
                });
                const data = resp && resp.data ? resp.data : resp;
                this.invoices = (data && data.invoices) || [];
            } catch (e) {
                this.errorMsg =
                    "Could not load distributor invoices. Please try again.";
            } finally {
                this.loading = false;
            }
        },
        async toggleLines(invoiceId) {
            if (this.expanded[invoiceId]) {
                delete this.expanded[invoiceId];
                return;
            }
            try {
                const resp = await api.get(
                    `/superadmin/distributor-invoices/${invoiceId}`
                );
                const data = resp && resp.data ? resp.data : resp;
                this.expanded[invoiceId] = (data && data.lines) || [];
            } catch (e) {
                this.errorMsg = "Could not load the invoice line detail.";
            }
        },
        async approve(invoice) {
            // Approve IS the issue: the server approves and issues in one
            // request (or refuses up front, leaving the draft).
            await this._issue(invoice, "approve", "approve and issue");
        },
        async retryIssue(invoice) {
            // An APPROVED invoice whose issue failed: the release route is
            // the same idempotent issue path, so a retry never bills twice.
            await this._issue(invoice, "release", "issue");
        },
        async _issue(invoice, verb, label) {
            this.busy = true;
            this.pendingId = invoice.id;
            this.errorMsg = "";
            delete this.actionErrors[invoice.id];
            delete this.actionResults[invoice.id];
            try {
                const resp = await api.post(
                    `/superadmin/distributor-invoices/${invoice.id}/${verb}`
                );
                const data = resp && resp.data ? resp.data : resp;
                this.actionResults[invoice.id] = issuedText(data);
            } catch (e) {
                // The server's message names the distributor and what is
                // missing (422), or says the invoice is approved and safe to
                // retry (502). Shown as sent; a generic line only when the
                // failure carried no message (a network error).
                const serverError =
                    e && e.response && e.response.data ? e.response.data.error : "";
                this.actionErrors[invoice.id] =
                    serverError ||
                    `Could not ${label} invoice #${invoice.id}. Please try again.`;
            } finally {
                this.pendingId = null;
                this.busy = false;
            }
            // Re-read rather than patching the row locally: the server owns
            // the transition. Success, a refusal and a failure each leave the
            // invoice in a state only the server knows for certain (issued,
            // still a draft, approved again, or parked as an exception).
            await this.fetchInvoices();
        },
        async runBatch() {
            this.busy = true;
            this.errorMsg = "";
            this.batchSummary = "";
            try {
                const resp = await api.post(
                    "/superadmin/distributor-invoices/run-batch"
                );
                const data = resp && resp.data ? resp.data : resp;
                this.batchSummary =
                    `${data.invoices_created} draft invoice(s), ` +
                    `${data.lines_created} line(s), ` +
                    `${formatMoney(data.total_cents)} total.`;
                await this.fetchInvoices();
            } catch (e) {
                this.errorMsg = "Could not assemble drafts. Please try again.";
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #1a202c;
}
.sa-di-intro {
    font-size: 14px;
    color: #4a5568;
    margin: 0 0 16px;
    max-width: 720px;
}
.sa-di-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}
.sa-di-summary {
    font-size: 13px;
    color: #2f855a;
}
.sa-di-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-di-state--error {
    color: #c53030;
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.sa-table th,
.sa-table td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
}
.sa-di-lines {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    background: #f7fafc;
}
.sa-di-lines th,
.sa-di-lines td {
    text-align: left;
    padding: 6px 10px;
}
.sa-di-warning-row td {
    color: #975a16;
    font-size: 13px;
    background: #fffff0;
}
.sa-di-outcomes {
    margin: 0 0 12px;
}
.sa-di-outcome {
    font-size: 13px;
    padding: 6px 10px;
    margin: 0 0 4px;
    border-radius: 4px;
}
.sa-di-outcome--ok {
    color: #2f855a;
    background: #f0fff4;
}
.sa-di-outcome--error {
    color: #c05621;
    background: #fffaf0;
}
.sa-di-exception-row td {
    color: #c05621;
    font-size: 13px;
    background: #fffaf0;
}
.sa-di-status {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    background: #edf2f7;
    color: #4a5568;
}
/* Parked: exception, payment_failed, needs_review. */
.sa-di-status--attention {
    color: #c05621;
    background: #fffaf0;
    border: 1px solid #fbd38d;
    font-weight: 600;
}
/* Uncollectible: still owed, but distinct from a parked invoice. */
.sa-di-status--written-off {
    color: #742a2a;
    background: #fed7d7;
    font-weight: 600;
}
.sa-di-status--issued,
.sa-di-status--paid {
    color: #2f855a;
    background: #f0fff4;
}
.sa-di-filter {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #4a5568;
}
.sa-di-filter-select {
    font-size: 13px;
    padding: 4px 8px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
}
.sa-di-btn {
    font-size: 13px;
    padding: 6px 12px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.sa-di-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.sa-di-btn--small {
    padding: 4px 8px;
}
.sa-di-btn--primary {
    border-color: #2b6cb0;
    color: #2b6cb0;
}
.sa-di-link {
    background: none;
    border: none;
    color: #2b6cb0;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
}
</style>
